'use strict';

import {thirdPartyTracking, initTrackingAgent} from '@edreamsodigeo/frontend-tracking-agent';
import {createStorageClient} from '@edreamsodigeo/frontend-cmp-client';
import {supportareaPageNamesMap} from './tms_datalayer_supportarea_pagenames';
import {bookingAdminPageNamesMap} from './tms_datalayer_bookingadmin';
import {bookingManagementPageNamesMap} from './tms_datalayer_bookingmanagement';
import {membershipPagesMap, membershipPagesPaths} from 'od-modules/models/tms_datalayer_membershipPages';
import {profilePagesMap, profilePagesPaths} from 'od-modules/models/tms_datalayer_profile';
const trackingUtils = require('../utils/tracking.utils');
const uuid = require('uuid');

const isPaymentPageOF2 = Odigeo.AB.isFecoRedirectionToPaymentDesktop || Odigeo.AB.isFecoRedirectionToPaymentMobile;

export default Odigeo.Model.extend({
    dataURL: 'datalayer',
    _datalayerTrackingClient: null,

    pageNamesMap: {
        'cms': 'cms',
        'home': 'homepage',
        'results': 'results',
        'dpresults_flights': 'results',
        'dpresults_accommodation': 'hotel_results',
        'upsell': 'hotels_upsell',
        'xsellproductselection': 'dp_upsell',
        'dpresults_accommodation_details': 'room_selection',
        'accommodation_details': 'room_selection',
        'flightsummary': 'summary',
        'trip_summary': 'summary',
        'booking': 'summary',
        'fare-rules': 'fare-rules',
        'details': 'details_extras',
        'passengersinfo': 'details_extras',
        'insurance': 'insurances',
        'insurances': 'insurances',
        'travel_insurances': 'insurances',
        'servicepack': 'service_options',
        'canx': 'cancellation_guarantee',
        'cancellation-guarantee': 'cancellation_guarantee',
        'prime': 'prime',
        'flex': 'flexible_dates',
        'flex_dates': 'flexible_dates',
        'flex_page': 'flexible_dates',
        'disruption_compensation': 'disruption-compensation',
        'disruption-compensation': 'disruption-compensation',
        'payment': 'payment',
        'confirmation': 'confirmation',
        'mytrips': 'mytrips',
        'mytripslogin': 'mytripslogin',
        'tripdetails': 'tripdetails',
        'register': 'register',
        'accountblocked': 'accountblocked',
        'flights/frequentpassengers': 'frequent_travelers',
        'accountcreditcards': 'account_credit_cards',
        'accountpreferences': 'account_preferences',
        'resetpassword': 'change_password',
        'primepostbooking': 'primepostbooking',
        'loginexpired': 'loginexpired',
        'additions': 'additions',
        'selection': 'ancillaries',
        'add': 'add',
        'room-selection': 'room_selection',
        'details-extras': 'details_extras',
        'pfhome': 'summary',
        'pfoffers': 'save-with-prime-plus',
        'pfpax': 'details-extras',
        'pfpayment': 'payment',
        'pfconfirmation': 'confirmation',
        'itinerary': 'itinerary',
        'summary': 'summary',
        'faqs': 'faqs',
        'extras_fast_track': 'fast-track',
        '/prime/reactivation/': 'prime-reactivation',
        'flights-bestprices': 'flights-bestprices',
        '/PB/fasttrack/home/': 'fasttrack-home',
        'tripdetails/flights': 'tripdetails',
        'tripdetails/hotels': 'tripdetails',
        'tripdetails/dynpack': 'tripdetails',
        'BF/tripdetails/flights': 'tripdetails',
        'BF/tripdetails/hotels': 'tripdetails',
        'BF/tripdetails/dynpack': 'tripdetails',
        ...profilePagesMap,
        ...membershipPagesMap,
        ...supportareaPageNamesMap,
        ...bookingAdminPageNamesMap,
        ...bookingManagementPageNamesMap,
    },
    postBookingAccommodationPagesMap: {
        xsellproductselection: 'results',
        pbpayment: 'payment',
    },

    url: function() {
        return this.getUrlByPageName(this.getPageName());
    },

    getUrlByPageName(pageName) {
        return Odigeo.Settings.getProperty('tagManagementUrlFormatter')(this.dataURL + '/' + pageName);
    },

    isConfirmationPage: function(options) {
        return this.getPageName(options) === 'confirmation';
    },

    isFastTrackPage: function(options) {
        return this.getPageName(options) === 'fasttrack-home';
    },

    getPageName(options) {
        if (Odigeo._.get(options, 'page_name')) {
            return this.pageNamesMap[options.page_name];
        } else {
            let pageName = Odigeo.Utils.isWebmasterPage() ? 'cms' : Odigeo.History.getPageNameFromFragment();
            pageName = pageName === 'ancillaries' ? Odigeo.History.getFragment().match(/(?:[^/]+)(?=$)/)[0] : pageName;
            const isAccommodationPostBooking = this.isAccommodationsPostBooking() && Odigeo._.has(this.postBookingAccommodationPagesMap, pageName);
            const pageNamesMap = isAccommodationPostBooking ? this.postBookingAccommodationPagesMap : this.pageNamesMap;
            return Odigeo._.get(pageNamesMap, pageName || 'home');
        }
    },

    getConvId: function() {
        return `${uuid.v4()}.${new Date().getTime()}`;
    },

    initialize: function() {
        const configParametersTrackingAgent = {
            generic: {
                visitID: Odigeo.SessionScope.visitId,
                storageClient: createStorageClient(Odigeo.SessionScope.brand, Odigeo.SessionScope.locale),
                isNativeInterface: Odigeo.SessionScope.isNativeInterface,
                dapiSessionId: Odigeo.SessionScope.dapiSessionId,
                locale: Odigeo.SessionScope.locale,
                flow: Odigeo.SessionScope.flow,
                device: Odigeo.Device.navigator.isIOSWebView ? 'IOS_' : 'ANDROID_',
                brand: Odigeo.SessionScope.brand,
                website: Odigeo.SessionScope.website,
                abs: [{
                    name: 'TMS_RECOVERY',
                    value: Odigeo.AB.getTmsRecovery,
                }],
            },
            datamesh: {
                context: '/travel',
            },
        };
        initTrackingAgent(configParametersTrackingAgent);
        this.syncTcVars();
    },

    getDatalayerTrackingClient: function() {
        if (!this._datalayerTrackingClient) {
            const {createDatalayerTrackingClient} = thirdPartyTracking;
            this._datalayerTrackingClient = createDatalayerTrackingClient();
        }
        return this._datalayerTrackingClient;
    },

    syncTcVars: function() {
        const tcVars = this.flatten(this.toJSON());
        this.getDatalayerTrackingClient().save({
            ...tcVars,
            ...this.getClientSideAttributes(),
        });
    },

    fireEvent: function(category, key, value) {
        this.getDatalayerTrackingClient().trackEvent({
            event_category: category,
            [key]: value,
        });
    },

    loadContainer: function() {
        const {trackTMSPage} = thirdPartyTracking;
        const tcVars = {
            ...this.flatten(this.toJSON()),
            ...this.getClientSideAttributes(),
        };
        if (tcVars.prime_added === 'false' || tcVars.prime_added === false) {
            delete tcVars.prime_added;
        }
        this.getDatalayerTrackingClient().save({
            user_prime_subscription: tcVars.user_prime_subscription,
            user_prime_subscription_type: tcVars.user_prime_subscription_type,
        });
        trackTMSPage(tcVars, {skipFetch: true});
        const pageName = this.get('page_name')
            || (window && window.tc_vars && window.tc_vars.page_name);
        trackingUtils.counterMetric('TMS_CONTAINER_RELOAD', {'<PAGE_NAME>': pageName});
    },

    isPageInWhitelist(options) {
        return Odigeo._.has(Odigeo._.invert(this.pageNamesMap), this.getPageName(options));
    },

    fetchDatalayer: function() {
        if (this.isPageInWhitelist()) {
            this.fetch({
                data: {product: this.getProduct()},
                success: this.loadContainer.bind(this),
                error: this.handleErrorOnBuildDatalayer.bind(this),
            });
        }
    },

    updateDatalayer: function(data) {
        if (this.isConfirmationPage(data)) {
            this.set({
                g_conv_id: this.getConvId(),
                ...this.flatten(data),
            });
            this.syncTcVars();
            return;
        }
        if (!Odigeo._.isEmpty(data)) {
            this.set(this.flatten(data));
            this.loadContainer();
        } else {
            this.fetchDatalayer();
        }
    },

    skipFetchDatalayerOnConfirm: function(options) {
        const product = this.getProduct(options);
        const isFlightsProduct = product === 'flights';
        const isSeatPB = product === 'seat_post_booking';
        return (isPaymentPageOF2 && isFlightsProduct) || isSeatPB;
    },
    isManageBookingPage: function(options, pageNamesMap) {
        const pageList = Object.keys(pageNamesMap);
        return pageList.filter((page) => {
            if (options !== undefined && options.page_name !== undefined)
                return options.page_name.includes(page);
        }).length > 0;
    },
    updateAndExtendDatalayer(extendedData, options) {
        const isConfirmPage = this.isConfirmationPage(options);
        const isOldManageBookingPage = this.isManageBookingPage(options, bookingManagementPageNamesMap);
        const isBookingAdminPage = this.isManageBookingPage(options, bookingAdminPageNamesMap);
        if (isConfirmPage) {
            extendedData = {
                ...extendedData,
                g_conv_id: this.getConvId(),
            };
        }
        if (isConfirmPage && this.skipFetchDatalayerOnConfirm(options)) {
            const confirmationTemplate = {
                flights: 'confirmation',
                accommodation: 'hotel-bf-confirmation',
                seat_post_booking: 'seat-PB',
            };
            const confirmationDataLayer = {
                ...extendedData,
                page_name: options.page_name,
                env_template: confirmationTemplate[this.getProduct(options)],
            };
            this.extendDataLayer(confirmationDataLayer);
        } else if (isOldManageBookingPage || isBookingAdminPage) {
            const mmbDataLayer = {
                ...extendedData,
                page_name: options.page_name,
                env_template: 'mybooking',
            };
            this.extendDataLayer(mmbDataLayer);
        } else {
            if (this.isPageInWhitelist(options)) {
                const fetchOptions = {
                    data: {product: this.getProduct(options)},
                    success: () => {
                        this.extendDataLayer(extendedData);
                    },
                    error: this.handleErrorOnBuildDatalayer.bind(this),
                };
                if (Odigeo._.get(options, 'page_name')) {
                    let pageName;

                    if (this.isMembershipPages(options)) {
                        pageName = this.getMembershipPagesMapKeyByValue(options.page_name);
                    } else if (this.isProfilePages(options)) {
                        pageName = this.getProfilePagesMapKeyByValue(options.page_name);
                    } else {
                        pageName = this.getPageName(options);
                    }

                    fetchOptions.url = this.getUrlByPageName(pageName);
                }
                this.fetch(fetchOptions);
            }
        }
    },

    extendDataLayer(extendedData) {
        this.set(this.flatten(extendedData));
        this.loadContainer();
    },

    handleErrorOnBuildDatalayer: function() {
        this.set('page_name', this.getPageName());
        this.loadContainer();
    },

    getClientSideAttributes: function() {
        const ofVersion = Odigeo.SessionScope.oneFrontVersion || '';
        return ({
            page_url: document.location.href,
            of_version: ofVersion,
        });
    },

    sendTmsEvent(eventCategory) {
        this.getDatalayerTrackingClient().trackEvent({
            event_category: eventCategory,
        });
    },

    isAccommodationsPostBooking: function() {
        return Odigeo.Utils.parseJson(Odigeo.Session.get('isAccommodationPostBooking'));
    },

    getProduct(options) {
        if (Odigeo._.get(options, 'product')) {
            return options.product;
        } else if (this.isAccommodationsPostBooking()) {
            return 'hotel_post_booking';
        } else if (this.isMembershipPages(options)) {
            return 'membership-pages';
        } else if (this.isFastTrackPage(options)) {
            return 'fasttrack_post_booking';
        } else if (this.isProfilePages(options)) {
            return 'flights'; // TODO: change to 'profile-pages' when rolling out user area page
        } else if (this.isTripdetailsPage(options)) {
            return this.getTripdetailsProduct(options);
        }
        return Odigeo.Utils.getFunnelInGlobalScope();
    },

    getTripdetailsProduct: (options) => {
        const productMap = {
            'flights': 'flights',
            'hotels': 'hotels',
            'dynpack': 'dp',
        };
        const parts = options.page_name.split('/');
        return `${parts[0] === 'BF' ? 'bf-' : ''}${productMap[parts[parts.length - 1]]}`;
    },
    flatten: function(obj) {
        const {flatten: flatted} = thirdPartyTracking;
        return flatted(obj || {});
    },

    isMembershipPages(options) {
        if (options && options.page_name) {
            return Object.values(membershipPagesPaths)
                .some((path) => options.page_name.includes(path));
        }
        return false;
    },

    isProfilePages(options) {
        if (options && options.page_name) {
            return Object.values(profilePagesPaths)
                .some((path) => options.page_name.includes(path));
        }
    },

    isTripdetailsPage(options) {
        if (!options || typeof options.page_name !== 'string') {
            return false;
        }

        const pageName = options.page_name;

        const tripdetailsPages = [
            'tripdetails/flights',
            'tripdetails/hotels',
            'tripdetails/dynpack',
            'BF/tripdetails/flights',
            'BF/tripdetails/hotels',
            'BF/tripdetails/dynpack',
        ];

        return tripdetailsPages.includes(pageName);
    },
    getMembershipPagesMapKeyByValue(pageName) {
        const page = membershipPagesMap[pageName];
        return page === null ? pageName : page;
    },

    getProfilePagesMapKeyByValue(pageName) {
        const page = profilePagesMap[pageName];
        return page === null ? pageName : page;
    },
});
